import { useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, federalIncomeTax } from "@/lib/finance";
import { fmtUSD, fmtPct } from "@/lib/format";
import { STATE_TAX_LIST, getStateTax } from "@/data/stateTax";
import { breadcrumbJsonLd } from "@/lib/seo";
import Seo from "@/components/Seo";
import EditorBadge from "@/components/EditorBadge";
import { MapPin } from "lucide-react";

// Programmatic SEO: /calculator/income-tax/:state
// 50 indexable pages — each with state-specific tax rate, standard deduction,
// and editorial commentary on local tax quirks.
export default function GeoIncomeTaxPage() {
    const { state: code } = useParams();
    const state = getStateTax(code || "");

    const [gross, setGross] = useState(85000);
    const [filing, setFiling] = useState("single");

    const result = useMemo(() => {
        if (!state) return null;
        const g = toNum(gross);
        const fed = federalIncomeTax(g, filing);
        let stateTax = 0;
        let stateTaxable = 0;
        if (!state.noTax) {
            const std = state.std?.[filing] ?? 0;
            stateTaxable = Math.max(0, g - std);
            stateTax = stateTaxable * (state.flatRate / 100);
        }
        const totalTax = fed.tax + stateTax;
        const takeHome = g - totalTax;
        const fica = g * 0.0765;
        return {
            fed: fed.tax,
            stateTax,
            stateTaxable,
            totalTax,
            takeHome,
            takeHomeAfterFica: g - totalTax - fica,
            fica,
            effectiveTotal: g > 0 ? (totalTax / g) * 100 : 0,
            effectiveState: g > 0 ? (stateTax / g) * 100 : 0,
        };
    }, [gross, filing, state]);

    if (!state) return <Navigate to="/calculator/income-tax" replace />;

    const title = `${state.name} Income Tax Calculator — 2025 Rates & Take-Home`;
    const description = state.noTax
        ? `${state.name} has NO state income tax — calculate your federal-only take-home pay. Free 2025 calculator with FICA breakdown.`
        : `Calculate ${state.name} state income tax (${state.flatRate}% effective) plus federal tax, FICA, and take-home pay. 2025 rates pre-filled.`;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: title,
            applicationCategory: "FinanceApplication",
            operatingSystem: "Any",
            description,
            areaServed: { "@type": "State", name: state.name, address: { "@type": "PostalAddress", addressRegion: state.upperCode || code.toUpperCase(), addressCountry: "US" } },
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
        breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Income Tax", path: "/calculator/income-tax" },
            { name: state.name, path: `/calculator/income-tax/${(code || "").toLowerCase()}` },
        ]),
    ];

    return (
        <>
            <Seo title={`${title} | FinCalc`} description={description} canonical={`/calculator/income-tax/${(code || "").toLowerCase()}`} jsonLd={jsonLd} ogSlug="income-tax" />

            <div className="container-editorial pt-8 pb-2">
                <nav className="flex items-center gap-2 text-xs text-muted-foreground" aria-label="Breadcrumb">
                    <Link to="/" className="hover:text-primary">Home</Link>
                    <span>/</span>
                    <Link to="/calculator/income-tax" className="hover:text-primary">Income Tax</Link>
                    <span>/</span>
                    <span className="text-primary">{state.name}</span>
                </nav>
            </div>

            <section className="container-editorial pt-4 pb-6 anim-fade-up">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent mb-3">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                    {state.name} · State income tax
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight text-balance">
                    {state.name} Income Tax Calculator
                </h1>
                <p className="mt-4 max-w-2xl text-lg text-foreground/80 leading-relaxed">
                    {state.noTax ? (
                        <>
                            <strong>{state.name} has no state personal income tax.</strong> Calculate your federal tax + FICA take-home pay below.
                        </>
                    ) : (
                        <>
                            Calculate your {state.name} state tax (top rate <strong>{state.topRate}%</strong>) plus federal income tax, FICA, and total take-home pay using 2025 rates.
                        </>
                    )}
                </p>
                <div className="mt-5"><EditorBadge /></div>
            </section>

            <CalculatorShell
                slug="income-tax"
                hideHeader
                suppressSeo
                explainer={
                    <>
                        <h2>{state.name} state income tax in 2025</h2>
                        <p>{state.note}</p>
                        {!state.noTax && (
                            <>
                                <h3>Quick reference</h3>
                                <ul>
                                    <li><strong>Top marginal rate:</strong> {state.topRate}%</li>
                                    <li><strong>Effective rate (most earners):</strong> ~{state.flatRate}%</li>
                                    <li><strong>Standard deduction (single):</strong> {fmtUSD(state.std?.single || 0, { decimals: 0 })}</li>
                                    <li><strong>Standard deduction (married joint):</strong> {fmtUSD(state.std?.married || 0, { decimals: 0 })}</li>
                                </ul>
                                <p>
                                    The calculator uses {state.flatRate}% as a simplified effective rate
                                    for typical wage earners. Real returns use full progressive brackets,
                                    so high or low incomes may pay slightly more or less than shown.
                                    For an exact figure, file with TurboTax / H&amp;R Block / a CPA.
                                </p>
                            </>
                        )}
                        {state.noTax && (
                            <>
                                <h3>What this means for your paycheck</h3>
                                <p>
                                    Living in {state.name} means your only federal-level wage taxes are
                                    federal income tax + FICA (7.65%). On a $85,000 single salary, that
                                    works out to roughly $14,000-$16,000 in total federal taxes —
                                    putting your take-home around $69,000-$71,000.
                                </p>
                                <p>
                                    The trade-off: most no-income-tax states recover revenue through
                                    higher property tax, sales tax, or corporate tax. Texas, for
                                    example, has the 6th-highest property tax in the US (2.1% effective).
                                    Florida has higher-than-average homeowners insurance. Always look
                                    at the full tax picture, not just income tax.
                                </p>
                            </>
                        )}
                        <h3>About FICA (always applies)</h3>
                        <p>
                            Regardless of state, every wage earner pays 7.65% in federal payroll tax:
                            6.2% Social Security (capped at $176,100 wages in 2025) and 1.45% Medicare.
                            High earners pay an additional 0.9% Medicare surtax above $200K single /
                            $250K joint.
                        </p>
                    </>
                }
            >
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                        <Field label="Gross annual income" prefix="$" value={gross} onChange={setGross} dataTestId="calc-input-gross" />
                        <div>
                            <label className="block font-sans text-[11px] uppercase tracking-[0.18em] text-primary/80 font-medium mb-1.5">Filing status</label>
                            <div className="inline-flex rounded-md border border-border overflow-hidden" role="tablist">
                                <button role="tab" aria-selected={filing === "single"} onClick={() => setFiling("single")} data-testid="calc-filing-single" className={`px-4 h-11 text-sm transition-colors ${filing === "single" ? "bg-primary text-primary-foreground" : "bg-background text-primary hover:bg-muted"}`}>Single</button>
                                <button role="tab" aria-selected={filing === "married"} onClick={() => setFiling("married")} data-testid="calc-filing-married" className={`px-4 h-11 text-sm transition-colors border-l border-border ${filing === "married" ? "bg-primary text-primary-foreground" : "bg-background text-primary hover:bg-muted"}`}>Married joint</button>
                            </div>
                        </div>
                        <div className="pt-3 space-y-1.5 text-xs text-muted-foreground font-mono tabular border-t border-border">
                            <div className="flex justify-between"><span>Federal income tax</span><span>{fmtUSD(result.fed, { decimals: 0 })}</span></div>
                            <div className="flex justify-between"><span>{state.name} state tax</span><span>{state.noTax ? "$0 (no state tax)" : fmtUSD(result.stateTax, { decimals: 0 })}</span></div>
                            <div className="flex justify-between"><span>FICA (Social Security + Medicare)</span><span>{fmtUSD(result.fica, { decimals: 0 })}</span></div>
                        </div>
                    </div>
                    <div className="lg:col-span-2">
                        <ResultStat
                            label="Annual take-home pay"
                            value={result.takeHomeAfterFica}
                            format={(n) => fmtUSD(n, { decimals: 0 })}
                            secondary={[
                                { label: "Total tax", value: result.totalTax + result.fica, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-total-tax" },
                                { label: "Effective rate", value: ((result.totalTax + result.fica) / Math.max(1, toNum(gross))) * 100, format: (n) => fmtPct(n, 1), testId: "calc-result-effective" },
                                { label: "Monthly take-home", value: result.takeHomeAfterFica / 12, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-monthly" },
                            ]}
                        />
                    </div>
                </div>

                {/* Other states cross-link */}
                <div className="mt-10 bg-card border border-border rounded-xl p-6">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-accent">All states</div>
                    <h3 className="font-serif text-2xl text-primary mt-1 mb-4">Compare another state</h3>
                    <div className="flex flex-wrap gap-2">
                        {STATE_TAX_LIST.filter((s) => s.code !== (code || "").toLowerCase()).slice(0, 16).map((s) => (
                            <Link key={s.code} to={`/calculator/income-tax/${s.code}`} className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/40 text-primary transition-colors">
                                {s.name}{s.noTax ? " (no tax)" : ""}
                            </Link>
                        ))}
                    </div>
                </div>
            </CalculatorShell>
        </>
    );
}
